/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import Downshift from 'downshift'
import { Button, InputGroup, Menu, MenuItem, Tag, Intent } from '@blueprintjs/core'
import { Icon, Flex, HighlightedText } from '@ui'
import { Mode } from '.'
import useSearch, { SearchItem, SearchItemType } from './useSearch'
import { IBuildingDetails, IFloorDetails, IPortfolioRegion, IPortfolioCity } from '@api'
import css from './search.module.scss'

export default function LocationSearch({
  mode,
  regions,
  selectCity,
  selectBuilding,
  selectFloor,
}: {
  mode: Mode
  regions: IPortfolioRegion[]
  selectCity: (city: IPortfolioCity) => void
  selectBuilding: (building: IBuildingDetails) => void
  selectFloor: (floor: IFloorDetails) => void
}) {
  const search = useSearch(regions, mode)

  const renderItem = ({ hash, type, item }: SearchItem, getItemProps: any, active: boolean) => {
    let menuItem
    switch (type) {
      case SearchItemType.CITY:
        menuItem = (
          <MenuItem
            active={active}
            shouldDismissPopover={false}
            {...getItemProps({
              item: { type, item },
              key: hash,
            })}
            data-item={item.name}
            text={<CityItem city={item as IPortfolioCity} query={search.query} />}
          />
        )
        break

      case SearchItemType.BUILDING:
        menuItem = (
          <MenuItem
            active={active}
            shouldDismissPopover={mode === Mode.SINGLE_BUILDING}
            {...getItemProps({
              item: { type, item },
              key: hash,
            })}
            text={<BuildingItem building={item as IBuildingDetails} query={search.query} />}
          />
        )
        break

      case SearchItemType.FLOOR:
        menuItem = (
          <MenuItem
            active={active}
            shouldDismissPopover={mode === Mode.SINGLE_FLOOR}
            {...getItemProps({
              item: { type, item },
              key: hash,
            })}
            text={<FloorItem floor={item as IFloorDetails} query={search.query} />}
          />
        )
        break

      default:
        menuItem = <div>----</div>
    }

    return (
      <div key={hash} style={{ borderBottom: '1px solid var(--border-color)' }}>
        {menuItem}
      </div>
    )
  }

  return (
    <Downshift
      onChange={(newValue) => {
        if (!newValue) return
        const { type, item } = newValue
        switch (type) {
          case SearchItemType.CITY:
            selectCity(item)
            break

          case SearchItemType.BUILDING: {
            // find building's city and select it
            let city: IPortfolioCity | undefined
            regions.forEach(({ cities }) => {
              if (!city) {
                city = cities.find((c) => {
                  if (c.buildings) {
                    return c.buildings.indexOf(item) >= 0
                  }
                  return false
                })
              }
            })
            if (city) selectCity(city)
            if (mode === Mode.SINGLE_BUILDING || mode === Mode.MULTI_BUILDING) {
              selectBuilding(item)
            }
            break
          }

          case SearchItemType.FLOOR: {
            // find city this floor belong to
            let city: IPortfolioCity | undefined
            regions.forEach(({ cities }) => {
              if (!city) {
                city = cities.find((c) => {
                  if (c.buildings) {
                    return c.buildings.find((b) => b.id === item.building.id)
                  }
                  return false
                })
              }
            })
            if (city) selectCity(city)
            selectFloor(item)
            break
          }

          default:
            break
        }
      }}
      itemToString={() => ''}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        isOpen,
        openMenu,
        closeMenu,
      }) => {
        return (
          <div style={{ padding: 5, zIndex: 1 }}>
            <InputGroup
              fill
              // @ts-ignore
              {...getInputProps({
                autoFocus: true,
                placeholder: 'Search...',
                value: search.query,
                // @ts-ignore
                onChange: ({ target: { value } }) => search.run(value),
                leftIcon: 'search',
                // @ts-ignore
                'data-input': 'location_search',
                // @ts-ignore
                rightElement: search.query && (
                  <Button
                    minimal
                    icon="cross"
                    onClick={() => {
                      search.run('')
                      closeMenu()
                    }}
                  />
                ),
                onFocus: (event) => {
                  if (search.query || search.items.length) {
                    openMenu()
                    event.target.select()
                  }
                },
              })}
            />
            {isOpen && search.query && (
              <div className={css.search_results_container} {...getMenuProps()}>
                <Menu>
                  {search.items.length ? (
                    search.items.map((item, i) =>
                      renderItem(item, getItemProps, i === highlightedIndex)
                    )
                  ) : (
                    <MenuItem
                      disabled
                      text={
                        <span>
                          No city or building found for{' '}
                          <strong style={{ color: 'var(--black)' }}>{search.query}</strong>{' '}
                        </span>
                      }
                    />
                  )}
                </Menu>
              </div>
            )}
          </div>
        )
      }}
    </Downshift>
  )
}

function CityItem({ city, query }: { city: IPortfolioCity; query?: string }) {
  const name = <HighlightedText text={city.name} query={query} />

  let state
  if (city.buildings[0] && city.buildings[0].state) {
    state = <HighlightedText text={city.buildings[0].state} query={query} />
  }
  return (
    <Flex align="center" justify="space-between">
      <Flex align="baseline">
        {name}
        {state && (
          <div className={css.small} style={{ fontSize: 12, marginLeft: 5 }}>
            {state}
          </div>
        )}
      </Flex>
      <Tag minimal>City</Tag>
    </Flex>
  )
}

function BuildingItem({ building, query }: { building: IBuildingDetails; query?: string }) {
  const name = <HighlightedText text={building.name} query={query} />

  let address
  if (building.address) {
    address = <HighlightedText text={building.address} query={query} />
  }
  return (
    <Flex align="center" justify="space-between">
      <div>
        {name}
        {address && (
          <div>
            <div className={css.small} style={{ fontSize: 12 }}>
              {address}
            </div>
          </div>
        )}
      </div>
      <Tag minimal intent={Intent.WARNING}>
        Building
      </Tag>
    </Flex>
  )
}

function FloorItem({ floor, query }: { floor: IFloorDetails; query?: string }) {
  const floorName = <HighlightedText text={floor.name} query={query} />
  const buildingName = <HighlightedText text={floor.building.name} query={query} />

  let address
  if (floor.building.address) {
    address = <HighlightedText text={floor.building.address} query={query} />
  }
  return (
    <Flex align="center" justify="space-between">
      <div>
        <Flex align="center">
          {floorName}
          <Icon name="long-arrow-right" style={{ margin: '0 5px' }} />
          {buildingName}
        </Flex>
        {address && (
          <div>
            <div className={css.small} style={{ fontSize: 12 }}>
              {address}
            </div>
          </div>
        )}
      </div>
      <Tag minimal intent={Intent.PRIMARY}>
        Floor
      </Tag>
    </Flex>
  )
}
